/**
 * lazyPage.ts
 *
 * Helper that bundles React.lazy() with ensureI18nChunks() so every route in
 * App.tsx declares its locale dependencies alongside the page import — you
 * can't forget one without breaking the page load.
 *
 * Usage:
 *   const Reports = lazyPage(() => import('./pages/Reports'), ['reports']);
 *   <Route path="reports" element={<Reports />} />
 *
 * The page factory is passed as a function literal so Vite can statically
 * analyse the import specifier and emit a per-page chunk — the same behaviour
 * you get from hand-rolled React.lazy. The i18n chunks are fetched in
 * parallel with the page chunk and both are awaited via Promise.all.
 */
import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import { ensureI18nChunks } from '@/i18n';

/**
 * Create a lazy-loaded page component that also preloads its i18n chunks.
 *
 * @param factory Page import factory, e.g. `() => import('./pages/Reports')`.
 *                Must be a direct arrow function with a literal import string
 *                so Vite can statically split it into its own chunk.
 * @param chunks  Locale chunk names this page needs (without `.json` suffix)
 *                — see `scripts/split-locales.mjs` KEY_TO_CHUNK for the list.
 *                Pass `[]` for pages that only use the bootstrap chunks.
 * @returns A lazy React component ready for `<Route element={...} />`.
 */
export function lazyPage<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
  chunks: readonly string[],
): LazyExoticComponent<T> {
  return lazy<T>(async () => {
    const [, mod] = await Promise.all([
      ensureI18nChunks(...chunks),
      factory(),
    ]);
    return mod;
  });
}
